// Unpublished Work © 2022-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {TextField} from '@deere/isg.component-library';
import ValidationInput from 'Ui/components/common/form/validation-input';
import {getLocalTimeFormats, validateDate} from 'Ui/components/manual-data/manual-data-utils';
import {getTimeFromDate, MANUAL_DATA_TIME_FORMAT} from 'Utils/time-utils';
import moment from 'moment';
import ReadOnlyWrapperComponent from 'Ui/components/common/form/read-only/read-only-wrapper-component';
import PinLocationInput from 'OnLabor/workboard/common/pin-location-input';
import DateTimePicker from 'Ui/components/common/date-picker/date-time-picker';

function enforceMinMaxDate(isEdit, date) {
    if (isEdit) {
        return {
            minDate: date,
            maxDate: date
        };
    }

    return {};
}

function WorkboardDetailsForm(props) {
    const {
        isEdit,
        membership,
        readOnly,
        setValid,
        setValues,
        translations,
        values,
        workboards
    } = props;

    const {
        validatedDate, validatedTime
    } = React.useMemo(() => {
        const validatedDate = validateDate(values.date, setValid);
        const validatedTime = validatedDate ? getTimeFromDate(validatedDate) : null;

        return {
            validatedDate,
            validatedTime
        };
    }, [values.date]);

    const onChange = React.useCallback((event) => {
        const {
            name, value
        } = event.target;

        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }, []);

    function workboardDateChange(formattedDate) {
        onChange({
            target: {
                name: 'date',
                value: formattedDate
            }
        });
    }

    return (
        <div className='add-workboard-form workboard-details-form'>
            <div className='settings-group mobile'>
                <ReadOnlyWrapperComponent
                    label={translations.NAME}
                    readOnly={readOnly}
                    readOnlyProps={{
                        readOnlyLabelClassName: 'title-1',
                        readOnlyValueClassName: 'workboard-details-readonly-value'
                    }}
                    value={values.name || ''}
                    wrappedComponent={ValidationInput}
                    wrappedProps={{
                        name: 'name',
                        onChange,
                        required: true,
                        setValid,
                        tabIndex: 0,
                        type: 'text'
                    }}
                /></div>
            <div className='settings-group mobile'>
                <ReadOnlyWrapperComponent
                    label={translations.DATE_AND_TIME}
                    readOnly={readOnly}
                    readOnlyProps={{
                        readOnlyLabelClassName: 'title-1',
                        readOnlyValueClassName: 'workboard-details-readonly-value',
                        value: moment(values.date).format('LLL')
                    }}
                    wrappedComponent={DateTimePicker}
                    wrappedProps={{
                        dateProps: {
                            ...enforceMinMaxDate(isEdit, validatedDate),
                            validatedDate
                        },
                        timeProps: {
                            timeFormat: getLocalTimeFormats(),
                            timeIntervals: 15,
                            validatedTime,
                            width: 130
                        },
                        onChange: (date) => workboardDateChange(date.format(MANUAL_DATA_TIME_FORMAT)),
                        translations
                    }}
                /></div>
            {
                membership.isMigrated &&
                <div className='settings-group mobile'>
                    <PinLocationInput
                        onChange={onChange}
                        readOnly={readOnly}
                        setValid={setValid}
                        translations={translations}
                        values={values}
                        workboards={workboards}
                    />
                </div>
            }
            <div className='settings-group mobile'>
                <ReadOnlyWrapperComponent
                    label={translations.NOTE}
                    readOnly={readOnly}
                    readOnlyProps={{
                        readOnlyLabelClassName: 'title-1',
                        readOnlyValueClassName: 'workboard-details-readonly-value'
                    }}
                    value={values.note || ''}
                    wrappedComponent={TextField}
                    wrappedProps={{
                        multiline: true,
                        className: 'notes-text-area',
                        name: 'note',
                        onChange,
                        rows: 3,
                        tabIndex: 0
                    }}
                /></div>
        </div>
    );
}

WorkboardDetailsForm.propTypes = {
    isEdit: PropTypes.bool,
    isMobile: PropTypes.bool,
    membership: PropTypes.membership,
    readOnly: PropTypes.bool,
    setLoading: PropTypes.func,
    setOperators: PropTypes.func,
    setValid: PropTypes.func,
    setValues: PropTypes.func,
    translations: PropTypes.translations,
    values: PropTypes.object,
    workboards: PropTypes.arrayOf(PropTypes.object)
};

export default MediaQuery(MOBILE_MEDIA_QUERY)(WorkboardDetailsForm);
